import React, { useState } from 'react';
import { Input, Select } from '@/components/common/FormField';
import { 
  RewardQueryParams,
  RewardCategory,
  RewardStatus,
  RewardVariant,
  STATUS_OPTIONS,
} from '@/app/types/rewards';
import { useRewardMetadata, getCategoryOptions, getVariantOptions } from '@/app/hooks/useRewardMetadata';

import './RewardFilters.scss';

interface RewardFiltersProps {
  filters: RewardQueryParams;
  onFiltersChange: (filters: RewardQueryParams) => void;
  onSearch: () => void;
  onClear: () => void;
}

export const RewardFilters: React.FC<RewardFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  onClear,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { categories, variants, loading: metadataLoading } = useRewardMetadata();
  const categoryOptions = getCategoryOptions(categories);
  const variantOptions = getVariantOptions(variants);

  const handleChange = <K extends keyof RewardQueryParams>(
    key: K,
    value: RewardQueryParams[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const hasActiveFilters = Boolean(
    filters.category ||
    filters.status ||
    filters.reward_variant ||
    filters.search
  );

  return (
    <div className="reward-filters" onKeyPress={handleKeyPress}>
      {/* Mobile Toggle Header */}
      <button 
        className="filters-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-expanded={!isCollapsed}
      >
        <span className="toggle-text">
          🔍 Filters
          {hasActiveFilters && <span className="active-indicator">●</span>}
        </span>
        <span className={`toggle-chevron ${isCollapsed ? '' : 'expanded'}`}>▼</span>
      </button>

      {/* Filter Content - Collapsible on Mobile */}
      <div className={`filters-content ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="filters-row">
          <Input
            label="Search"
            value={filters.search || ''}
            onChange={(val) => handleChange('search', val || undefined)}
            placeholder="Search by key, name, or title"
          />

          <Select
            label="Category"
            value={filters.category || ''}
            onChange={(val) => handleChange('category', (val as RewardCategory) || undefined)}
            options={categoryOptions}
            disabled={metadataLoading}
          />

          <Select
            label="Status"
            value={filters.status || ''}
            onChange={(val) => handleChange('status', (val as RewardStatus) || undefined)}
            options={STATUS_OPTIONS.map(opt => ({
              value: opt.value,
              label: opt.label,
            }))}
          />

          <Select
            label="Variant"
            value={filters.reward_variant || ''}
            onChange={(val) => handleChange('reward_variant', (val as RewardVariant) || undefined)}
            options={variantOptions}
            disabled={metadataLoading}
          />

          <div className="filter-actions">
            <button className="btn-primary" onClick={onSearch}>
              Search
            </button>
            <button className="btn-secondary" onClick={onClear}>
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardFilters;

